import {
  BodyText,
  Card,
  CardContent,
  Collapsible,
  HeaderText,
  Skeleton,
} from "@/components/ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { cn } from "@/lib/utils";
import { useUser } from "@/network/useUser";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { canAccessAdminPanel } from "./access";
import { DirectLookup } from "./components/DirectLookup";
import { PatientBrowse } from "./components/PatientBrowse";
import { ResourceDetail } from "./components/ResourceDetail";
import { VersionHistory } from "./components/VersionHistory";
import type { AdminSearch } from "./schemas";

/**
 * Admin panel: find a FHIR resource (by id or via patient browse), view its
 * current value, and step through its version history with a clinical or
 * engineering diff.
 */
export const Admin = () => {
  const { data: user } = useUser();
  const search = useSearch({ from: "/admin" });
  const navigate = useNavigate({ from: "/admin" });

  const role = user?.professional.role;

  // Each detail section can be collapsed to focus on the other; when only one
  // is open it expands to full width.
  const [openResource, setOpenResource] = useState(true);
  const [openHistory, setOpenHistory] = useState(true);

  const onNavigate = (partial: Partial<AdminSearch>) =>
    navigate({
      search: (prev) => {
        // Merge, then drop cleared keys so they're removed from the URL
        // (e.g. selecting a new patient clears resourceType/resourceId).
        const next = { ...prev, ...partial };
        for (const key of Object.keys(next) as (keyof AdminSearch)[]) {
          if (next[key] === undefined) delete next[key];
        }
        return next;
      },
    });

  const onInspect = (resourceType: string, resourceId: string) =>
    onNavigate({ resourceType, resourceId });

  const { resourceType, resourceId } = search;

  // Wait for the user before rendering — avoids a flash of the full panel
  // before the client-side access check resolves.
  if (!user) {
    return (
      <div className="p-6">
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  // Client-side POC gate. The real boundary lands in v2 on the gateway.
  if (!canAccessAdminPanel(role)) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-error bg-error/10 p-4">
          <BodyText textColor="error">
            You don't have access to the Admin panel.
          </BodyText>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full space-y-6 overflow-auto p-6 pb-12">
      <HeaderText size="xl">FHIR Admin</HeaderText>

      <Card>
        <CardContent className="pt-6">
          <Tabs
            defaultValue={resourceId && !search.patientId ? "id" : "patient"}
          >
            <TabsList>
              <TabsTrigger value="patient">By patient</TabsTrigger>
              <TabsTrigger value="id">By resource id</TabsTrigger>
            </TabsList>
            <TabsContent value="patient" className="pt-4">
              <PatientBrowse
                search={search}
                onNavigate={onNavigate}
                onInspect={onInspect}
              />
            </TabsContent>
            <TabsContent value="id" className="pt-4">
              <DirectLookup
                initialType={resourceType}
                initialId={resourceId}
                onInspect={onInspect}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {resourceType && resourceId && (
        <div
          className={cn(
            "grid w-full grid-cols-1 items-start gap-6",
            openResource && openHistory && "xl:grid-cols-2",
          )}
        >
          <Collapsible
            title="Current value"
            controlled
            open={openResource}
            onOpenChange={setOpenResource}
            className="min-w-0"
          >
            <ResourceDetail
              key={`${resourceType}/${resourceId}`}
              resourceType={resourceType}
              resourceId={resourceId}
            />
          </Collapsible>
          <Collapsible
            title="Version history"
            controlled
            open={openHistory}
            onOpenChange={setOpenHistory}
            className="min-w-0"
          >
            <VersionHistory
              key={`${resourceType}/${resourceId}`}
              resourceType={resourceType}
              resourceId={resourceId}
            />
          </Collapsible>
        </div>
      )}
    </div>
  );
};
